import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { EditorState, EditorSelection, Compartment, StateField, StateEffect, Annotation } from '@codemirror/state';
import { EditorView, Decoration, ViewPlugin, WidgetType, keymap } from '@codemirror/view';
import { history, defaultKeymap, historyKeymap, moveLineUp, moveLineDown } from '@codemirror/commands';
import { formatActions } from '../lib/formatTarget';
import { alwaysHandled } from '../lib/keymapHelpers';
import { startsWithHighlightSpan } from '../lib/highlightMarkers';
import { registerEditorView } from '../lib/cmRegistry';
import { consumedSpan } from '../lib/reminderSpan';
import { findEmphasis } from '../lib/emphasisSpans';
import { setCaretLine, clearCaretLineFor, isBellLine } from '../lib/caretReminder';
import './MarkdownEditor.css';

// MarkdownEditor — Walter 2026-07-06. The CodeMirror-6 live-preview editor
// that (behind a feature flag) will eventually replace the tile <textarea>.
// Built as a SWAPPABLE component with a narrow contract so the app's
// reminder-capture / edit-lock / highlight plumbing can talk to it the same
// way it talks to the textarea:
//
//   props:  value, onChange(text), onBlur(), onFocus(), readOnly, autoFocus
//   ref:    { focus, getValue, getSelection, setSelection, insertText,
//             coordsAtPos, view }
//
// The Obsidian-style rendering (hide `#`, checkbox widgets, hide `**`) is a
// DISPLAY layer — the document underneath is always plain text, unchanged.
// This is a first cut for the flagged /editor-lab preview; the imperative
// contract is stubbed-in and gets fleshed out at integration time.

class CheckboxWidget extends WidgetType {
  constructor(checked, togglePos) { super(); this.checked = checked; this.togglePos = togglePos; }
  eq(o) { return o.checked === this.checked && o.togglePos === this.togglePos; }
  toDOM(view) {
    // Always render the same DOM (a box holding a checkmark), toggling only
    // a class — so checking/unchecking never adds/removes a node and the line
    // can't reflow horizontally. The ✓ is hidden via opacity when unchecked.
    const b = document.createElement('span');
    b.className = 'md-cbx' + (this.checked ? ' on' : '');
    b.setAttribute('contenteditable', 'false');
    b.textContent = '✓';
    const pos = this.togglePos, checked = this.checked;
    b.addEventListener('mousedown', (e) => {
      e.preventDefault(); e.stopPropagation();
      view.dispatch({ changes: { from: pos, to: pos + 1, insert: checked ? ' ' : 'x' } });
    });
    return b;
  }
  ignoreEvent() { return false; }
}
class BulletWidget extends WidgetType {
  toDOM() { const s = document.createElement('span'); s.className = 'md-bullet'; s.textContent = '•'; return s; }
  ignoreEvent() { return false; }
}
// Monochrome bell — replaces the rendered 🔔 on non-cursor reminder lines
// (Walter 2026-07-16: the colour emoji clashed with the new monochrome Format
// icons; the STORED text keeps the 🔔, this is display-only). Same SVG path as
// FormatMenu's BellIcon.
class BellWidget extends WidgetType {
  eq() { return true; }
  toDOM() {
    const s = document.createElement('span');
    s.className = 'md-bellicon';
    s.setAttribute('contenteditable', 'false');
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none">'
      + '<path d="M8 2a3.2 3.2 0 0 0-3.2 3.2c0 2.4-.9 3.6-1.5 4.2-.3.3-.1.8.3.8h8.8c.4 0 .6-.5.3-.8-.6-.6-1.5-1.8-1.5-4.2A3.2 3.2 0 0 0 8 2z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>'
      + '<path d="M6.6 12.4a1.5 1.5 0 0 0 2.8 0" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
    return s;
  }
  ignoreEvent() { return false; }
}

function intersects(consumed, s, e) {
  for (const [a, b] of consumed) { if (s < b && e > a) return true; }
  return false;
}

function addInline(deco, text, lineFrom, fromRel, onLine) {
  if (onLine) return;
  const consumed = [];

  // Inline links [text](url) → show just `text` (clickable), hide the markup.
  const linkRe = /\[([^\]]+)\]\(([^)\s]+)\)/g; let lm;
  while ((lm = linkRe.exec(text))) {
    const s = lineFrom + lm.index, e = s + lm[0].length;
    if (s < fromRel) continue;
    const tStart = s + 1, tEnd = tStart + lm[1].length;
    deco.push(Decoration.replace({}).range(s, tStart));          // "["
    deco.push(Decoration.mark({ class: 'md-link', attributes: { 'data-url': lm[2] } }).range(tStart, tEnd));
    deco.push(Decoration.replace({}).range(tEnd, e));            // "](url)"
    consumed.push([lm.index, lm.index + lm[0].length]);
  }

  // Bare URL autolinks (not already inside a []() link).
  const urlRe = /https?:\/\/[^\s)]+/g; let um;
  while ((um = urlRe.exec(text))) {
    if (intersects(consumed, um.index, um.index + um[0].length)) continue;
    const s = lineFrom + um.index, e = s + um[0].length;
    if (s < fromRel) continue;
    deco.push(Decoration.mark({ class: 'md-link', attributes: { 'data-url': um[0] } }).range(s, e));
    consumed.push([um.index, um.index + um[0].length]);
  }

  // Paired HIGHLIGHT spans — `^word^` green, `^^word^^` blue, `^^^word^^^`
  // red. Walter 2026-08-22: "we're pretty much mirroring bold, italics, and
  // strikethrough here", so they're parsed alongside them and the markers hide
  // the same way. An UNPAIRED caret matches nothing and stays visible, which
  // is what makes a stray `^` harmless.
  const hlRe = /(\^{1,3})([^^\n]+?)\1/g; let hm;
  while ((hm = hlRe.exec(text))) {
    if (intersects(consumed, hm.index, hm.index + hm[0].length)) continue;
    const s = lineFrom + hm.index, e = s + hm[0].length;
    if (s < fromRel) continue;
    const ml = hm[1].length;
    deco.push(Decoration.replace({}).range(s, s + ml));
    deco.push(Decoration.mark({ class: 'md-hl-' + ml }).range(s + ml, e - ml));
    deco.push(Decoration.replace({}).range(e - ml, e));
    consumed.push([hm.index, hm.index + hm[0].length]);
  }

  // Emphasis: **bold** / __bold__ / *ital* / _ital_ / ~~strike~~. The rule
  // itself lives in lib/emphasisSpans so it can be unit-tested (this module
  // pulls in CodeMirror, which Jest cannot load). Walter 2026-10-09: that is
  // also where underscores INSIDE a word stopped counting - his band's handle
  // @at_the_sundowners was rendering as "at" + italic "the" + "sundowners".
  for (const { start, end, markerLen, cls } of findEmphasis(text)) {
    if (intersects(consumed, start, end)) continue;
    const s = lineFrom + start, e = lineFrom + end;
    if (s < fromRel) continue;
    deco.push(Decoration.replace({}).range(s, s + markerLen));
    deco.push(Decoration.mark({ class: cls }).range(s + markerLen, e - markerLen));
    deco.push(Decoration.replace({}).range(e - markerLen, e));
  }
}

function addLine(deco, line, onLine) {
  const t = line.text, f = line.from;

  // Peel leading line MODIFIERS — highlight (^) and header (#) — in either
  // order. Highlight is a UNIVERSAL modifier: it stacks on whatever base type
  // follows (checkbox, reminder, @date, bullet, plain). A header consumes the
  // line (no further base type). rf = doc offset of the text after the prefix.
  // Walter 2026-08-22: a leading marker that CLOSES later is a span, not a
  // line modifier — `^important^ and the rest` highlights one word. Only an
  // UNCLOSED leading marker still washes the whole paragraph. addInline draws
  // the span; skip the peel here so we don't do both.
  const leadingIsSpan = startsWithHighlightSpan(t);
  let pre = 0, hlClass = null, headerClass = null, mm;
  for (;;) {
    const sub = t.slice(pre);
    if (!leadingIsSpan && !hlClass && (mm = sub.match(/^(\^{1,3})\s?/))) { hlClass = 'md-hl-' + mm[1].length; pre += mm[0].length; continue; }
    if (!headerClass && (mm = sub.match(/^(#{1,6})\s+/))) { headerClass = 'md-h' + Math.min(mm[1].length, 3); pre += mm[0].length; continue; }
    break;
  }
  const rf = f + pre, rest = t.slice(pre);
  const lineMods = () => {
    // Header = full-width band (document structure). Highlight = a background
    // on the TEXT ONLY (a mark over the content), not the whole line width.
    if (headerClass) deco.push(Decoration.line({ class: headerClass }).range(f));
    if (hlClass && line.to > rf) deco.push(Decoration.mark({ class: hlClass }).range(rf, line.to));
  };
  const hidePre = () => { if (!onLine && pre > 0) deco.push(Decoration.replace({}).range(f, rf)); };

  // Header consumes the line (with optional highlight stacked).
  if (headerClass) {
    lineMods(); hidePre();
    addInline(deco, t, f, rf, onLine);
    return;
  }

  let m;
  // Reminder bell — the STORED text keeps the 🔔; the DISPLAY always swaps it
  // for the monochrome BellWidget, cursor line included. (Round 3 tried CSS
  // font-variant-emoji for the cursor line, but Android's emoji font has no
  // monochrome bell glyph so the colour emoji leaked through — Walter
  // 2026-07-17 s2. The widget is font-independent; backspace still deletes the
  // underlying emoji as one unit, and the rest of the line stays raw text.)
  if ((m = rest.match(/^🔔\s?/))) {
    lineMods();
    deco.push(Decoration.replace({ widget: new BellWidget() }).range(rf, rf + '🔔'.length));
    if (!onLine) hidePre();
    // Walter 2026-09-15: the date highlight shows on the caret's line too.
    // It used to sit inside the !onLine rule, which is for markup that is
    // HIDDEN off the caret line; this only colours, so it just vanished while
    // he typed - "when I'm in the line, the orange highlight disappears".
    // .md-rdate has no padding/margin and the real 600 weight keeps monospace
    // widths, so it can't shift text as the colon is typed.
    {
      const after = rest.slice(m[0].length);
      const ds = rf + m[0].length;
      const sep = after.indexOf(': ');
      if (sep > 0) {
        // Canonical line: the tidy-up put a ": " after the date/time, and
        // everything before it is the timing. No parse needed - this is the
        // common case and it runs on every keystroke.
        deco.push(Decoration.mark({ class: 'md-rdate' }).range(ds, ds + sep));
      } else {
        // Walter 2026-09-26: a bell line that never went through the tidy-up
        // (typed before it existed, or pasted) used to show NO wash at all,
        // even when the reminder was understood perfectly - his "every Wed and
        // Sun 5/17 9am book squash". Wash exactly what the parser consumed,
        // so what we ignored stays plain: "just not highlighting the 5/17 if
        // we are not processing it". Parse is cached (lib/reminderSpan).
        const span = consumedSpan(after);
        if (span) deco.push(Decoration.mark({ class: 'md-rdate' }).range(ds + span.start, ds + span.end));
      }
    }
    addInline(deco, t, f, rf + m[0].length, onLine);
    return;
  }
  // @date log line.
  if ((m = rest.match(/^@(?:\d{4}-\d{2}-\d{2}|today|yesterday|tomorrow)\b\s?/i))) {
    lineMods();
    if (!onLine) {
      hidePre();
      const tokEnd = rf + m[0].replace(/\s$/, '').length;
      deco.push(Decoration.mark({ class: 'md-logdate' }).range(rf, tokEnd));
    }
    addInline(deco, t, f, rf + m[0].length, onLine);
    return;
  }
  // Checkbox — dash optional (`- [ ]` GFM, or legacy bare `[x]`). Highlight stacks
  // → a highlighted checkbox renders as an urgent/colored todo.
  if ((m = rest.match(/^(\s*[-*]\s+)?\[( |x|X)\]\s?/))) {
    lineMods();
    const checked = /x/i.test(m[2]);
    const markTo = rf + m[0].length;
    const togglePos = rf + m[0].indexOf('[') + 1;
    if (!onLine) {
      hidePre();
      deco.push(Decoration.replace({ widget: new CheckboxWidget(checked, togglePos) }).range(rf, markTo));
      if (checked) deco.push(Decoration.line({ class: 'md-done' }).range(f));
    }
    addInline(deco, t, f, markTo, onLine);
    return;
  }
  // Bullet.
  if ((m = rest.match(/^(\s*[-*]\s+)/))) {
    lineMods();
    if (!onLine) {
      hidePre();
      deco.push(Decoration.replace({ widget: new BulletWidget() }).range(rf, rf + m[0].length));
    }
    addInline(deco, t, f, rf + m[0].length, onLine);
    return;
  }
  // Plain (possibly highlighted).
  if (hlClass) {
    lineMods(); hidePre();
    addInline(deco, t, f, rf, onLine);
    return;
  }
  addInline(deco, t, f, f, onLine);
}

function buildDeco(view) {
  const deco = [];
  const sel = view.state.selection.main;
  const doc = view.state.doc;
  // Only reveal raw markup on the cursor's line while the editor is FOCUSED.
  // An unfocused editor (e.g. a board tile you haven't clicked into) still has
  // a selection at offset 0, which would otherwise leave the first line showing
  // raw `#`/`**` — so treat "no line is active" when unfocused and render the
  // whole doc as clean preview. (Walter 2026-07-12: tile preview fix.)
  const focused = view.hasFocus;
  for (const { from, to } of view.visibleRanges) {
    let n = doc.lineAt(from).number;
    const end = doc.lineAt(to).number;
    for (; n <= end; n++) {
      const line = doc.line(n);
      const onLine = focused && sel.from <= line.to && sel.to >= line.from;
      addLine(deco, line, onLine);
    }
  }
  return Decoration.set(deco, true);
}

const livePreview = ViewPlugin.fromClass(class {
  constructor(view) { this.decorations = buildDeco(view); }
  // Rebuild on focus change too, so lines flip between raw (focused, on the
  // cursor line) and rendered (blurred) immediately.
  update(u) { if (u.docChanged || u.selectionSet || u.viewportChanged || u.focusChanged) this.decorations = buildDeco(u.view); }
}, { decorations: v => v.decorations });

// Formatting commands (Ctrl/Cmd-B / -I / -K). Wrap the selection in the marker
// (empty selection → markers with the caret between them, ready to type).
function wrapSelection(view, mark) {
  const n = mark.length, docLen = view.state.doc.length;
  view.dispatch(view.state.changeByRange((range) => {
    const { from, to } = range;
    const inner = view.state.sliceDoc(from, to);
    // Already wrapped, markers inside the selection → unwrap.
    if (inner.length >= 2 * n && inner.startsWith(mark) && inner.endsWith(mark)) {
      const stripped = inner.slice(n, inner.length - n);
      return { changes: { from, to, insert: stripped }, range: EditorSelection.range(from, from + stripped.length) };
    }
    // Markers sit just outside the selection (or an empty caret between them) → remove them.
    const before = from - n >= 0 ? view.state.sliceDoc(from - n, from) : '';
    const after = to + n <= docLen ? view.state.sliceDoc(to, to + n) : '';
    if (before === mark && after === mark) {
      return {
        changes: [{ from: from - n, to: from, insert: '' }, { from: to, to: to + n, insert: '' }],
        range: EditorSelection.range(from - n, to - n),
      };
    }
    // Otherwise wrap. Empty selection → caret lands between the markers.
    return {
      changes: { from, to, insert: mark + inner + mark },
      range: EditorSelection.range(from + n, to + n),
    };
  }));
  view.focus();
  return true;
}
// Insert [label](url) at [from,to], padding a space only when it'd glue onto
// an adjacent word. Caret lands just after the inserted link.
function doInsertLink(view, from, to, label, url) {
  const docLen = view.state.doc.length;
  const prev = from > 0 ? view.state.sliceDoc(from - 1, from) : '';
  const next = to < docLen ? view.state.sliceDoc(to, to + 1) : '';
  const padBefore = prev && !/\s/.test(prev) ? ' ' : '';
  const padAfter = next && !/\s/.test(next) ? ' ' : '';
  const insert = padBefore + '[' + label + '](' + url + ')' + padAfter;
  view.dispatch({ changes: { from, to, insert }, selection: EditorSelection.cursor(from + insert.length) });
}

// Cap an auto-suggested label so a long URL slug doesn't produce an absurd
// label (Walter 2026-07-12). Trims at a word boundary within `max` chars — never
// mid-word — and only affects the SUGGESTION; labels the user types are left
// alone.
function capLabel(s, max = 40) {
  if (!s || s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return (sp > max * 0.5 ? cut.slice(0, sp) : cut).trim();
}

// Suggest a link label from the URL: a humanised last path segment
// ("my-great-post" → "my great post"), else the hostname ("www.x.com" → "x.com").
function suggestLabel(url) {
  try {
    const u = new URL(/^[a-z]+:\/\//i.test(url) ? url : 'https://' + url);
    const seg = u.pathname.split('/').filter(Boolean).pop();
    if (seg) {
      const clean = decodeURIComponent(seg).replace(/\.[a-z0-9]{1,5}$/i, '').replace(/[-_]+/g, ' ').trim();
      if (clean) return capLabel(clean);
    }
    return capLabel(u.hostname.replace(/^www\./, ''));
  } catch (e) { return capLabel(url); }
}

// Does this look like a URL we can drop straight into a link? No whitespace,
// and either a scheme (https://…) or a bare domain (example.com/path).
function isLikelyUrl(s) {
  if (!s || /\s/.test(s) || s.length > 2000) return false;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return true;      // has a scheme
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(s);      // bare domain/path
}

// Read the clipboard and return it only if it looks like a URL. Best-effort:
// browsers may deny readText (Firefox blocks it for page scripts) or prompt for
// permission — any failure resolves to null so the caller falls back to the
// manual URL prompt.
async function readClipboardUrl() {
  try {
    if (!navigator.clipboard || !navigator.clipboard.readText) return null;
    const t = ((await navigator.clipboard.readText()) || '').trim();
    return isLikelyUrl(t) ? t : null;
  } catch {
    return null;
  }
}

// Ctrl/Cmd-K (or the toolbar 🔗) → insert a link at the caret.
//   - If the CLIPBOARD holds a URL, skip the URL step:
//       · text selected → insert [selection](clipboardURL) immediately;
//       · nothing selected → jump straight to the "Link text" step, pre-filled.
//   - Otherwise a two-step popup: 1) paste/type the URL, 2) name the link.
// If text is selected it's used as the label. Esc cancels; falls back to an
// inline placeholder if the caret has no coords. Walter 2026-07-12.
function insertLink(view) {
  const sel = view.state.selection.main;
  const label0 = view.state.sliceDoc(sel.from, sel.to);
  const coords = view.coordsAtPos(sel.head);

  // Build + show the popup. `prefillUrl` (from the clipboard) pre-fills and
  // selects the URL field so it can be confirmed with Enter — we always start
  // at the URL step so the user sees/approves the link before naming it
  // (Walter 2026-07-12: confirm the URL first, don't skip to the name).
  const openPopup = (prefillUrl) => {
    if (!coords) {
      doInsertLink(view, sel.from, sel.to, label0 || 'link_label', prefillUrl || 'url');
      view.focus();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'md-linkpop';
    const hint = document.createElement('div');
    hint.className = 'md-linkpop-hint';
    hint.textContent = 'Link URL';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Paste a URL, then Enter';
    if (prefillUrl) input.value = prefillUrl;
    pop.appendChild(hint);
    pop.appendChild(input);
    pop.style.top = (coords.bottom + 4) + 'px';
    pop.style.left = coords.left + 'px';
    document.body.appendChild(pop);

    let done = false, step = 'url', urlVal = '';
    const close = () => { if (done) return; done = true; pop.remove(); };
    const finish = (label, url) => { close(); doInsertLink(view, sel.from, sel.to, label, url); view.focus(); };
    const submit = () => {
      const val = input.value.trim();
      if (step === 'url') {
        if (!val) { close(); view.focus(); return; }
        urlVal = val;
        if (label0) { finish(label0, urlVal); return; } // selection is the label → done
        step = 'name';
        hint.textContent = 'Link text';
        input.placeholder = 'Link text';
        input.value = suggestLabel(urlVal);
        input.select();
        return;
      }
      finish(val || suggestLabel(urlVal) || urlVal, urlVal);
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); view.focus(); }
    });
    input.addEventListener('blur', () => setTimeout(close, 120));
    input.focus();
    // Pre-filled URL → select it so Enter confirms (or the user types over it).
    if (prefillUrl) input.select();
  };

  // Clipboard-aware: a URL on the clipboard pre-fills the URL field (still
  // requires an Enter to confirm). readClipboardUrl() never rejects, so a
  // denied/empty/non-URL clipboard just opens the empty manual prompt.
  readClipboardUrl().then((clipUrl) => openPopup(clipUrl || null));
  return true;
}
const formatKeymap = [
  { key: 'Mod-b', run: (v) => wrapSelection(v, '**') },
  { key: 'Mod-i', run: (v) => wrapSelection(v, '*') },
  { key: 'Mod-k', run: insertLink },
  // CM6's defaultKeymap binds Shift-Alt-Arrow to COPY line (VS Code
  // convention) — in this app Alt+Shift+↑/↓ has always meant MOVE line
  // (textarea path + Format menu). Override before the defaults so the CM6
  // editor matches (Walter 2026-07-19: "duplicates rather than moves").
  //
  // Walter 2026-08-10: …and SWALLOW the key even when the move is a no-op.
  // CM6 keymaps fall through on a `false` return, and moveLineUp returns
  // false on the first line (nothing above to swap with) — so at the top edge
  // the key fell through to defaultKeymap's copyLineUp and DUPLICATED the
  // line instead. His report: "when I move stuff at the top edge of the tile
  // it starts duplicating the line." Bottom edge had the same bug via
  // copyLineDown. #152 fixed the always-duplicates case; this closes the
  // edges. The textarea path was never affected — it preventDefaults on the
  // no-op edge rather than returning false.
  { key: 'Alt-Shift-ArrowUp', run: alwaysHandled(moveLineUp) },
  { key: 'Alt-Shift-ArrowDown', run: alwaysHandled(moveLineDown) },
  // App conventions beat CM6/VS Code idioms (Walter 2026-07-19: "stick with
  // ours") — Ctrl+H cycles the line highlight, Shift+Ctrl+H toggles done,
  // same as the textarea. Ctrl explicitly, NOT Mod: ⌘H is macOS Hide (the
  // #98 lesson). Routed through formatActions, so they work wherever the
  // editor registered as the format target (panels; /list/:id never had
  // these — parity kept). Always swallowed: Ctrl+H is browser history.
  { key: 'Ctrl-h', run: () => { formatActions.cycleHighlight(); return true; } },
  { key: 'Shift-Ctrl-h', run: () => { formatActions.toggleDone(); return true; } },
];

// ── FIND HIGHLIGHTS (Walter 2026-08-24) ──────────────────────────────────
// Ctrl+F paints every hit at once over a textarea via FindOverlay's mirror
// layer. That technique can't work here — CodeMirror hides syntax markers, so
// a mirror would be measuring different text — and my first cut instead moved
// the SELECTION to each hit. Walter: "it never actually highlights the one I
// can see", and he was right: CodeMirror only paints a selection while the
// view has focus, and the find bar deliberately keeps the keyboard. So the
// matches are drawn as decorations, which don't care about focus, in the same
// colours FindOverlay uses.
const setFindMatchesEffect = StateEffect.define();

const findHit = Decoration.mark({ class: 'cm-find-hit' });
const findHitCurrent = Decoration.mark({ class: 'cm-find-hit cm-find-hit-current' });

const findMatchField = StateField.define({
  create() { return Decoration.none; },
  update(deco, tr) {
    for (const e of tr.effects) {
      if (!e.is(setFindMatchesEffect)) continue;
      const { ranges, current } = e.value;
      const len = tr.state.doc.length;
      const built = [];
      (ranges || []).forEach((r, i) => {
        // Offsets come from a search of a doc snapshot; clamp so an edit
        // that landed in between can't throw.
        const from = Math.max(0, Math.min(r.from, len));
        const to = Math.max(from, Math.min(r.to, len));
        if (to > from) built.push((i === current ? findHitCurrent : findHit).range(from, to));
      });
      return Decoration.set(built, true);
    }
    // Keep the highlights glued to the text through edits.
    return tr.docChanged ? deco.map(tr.changes) : deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

/**
 * setFindMatches(view, ranges, current) — show find hits in this editor.
 * `ranges` is [{from, to}] in document offsets, `current` the index of the
 * one being stepped to (-1 for none). Pass an empty array to clear.
 */
export function scrollFindMatchIntoView(view, pos) {
  if (!view) return;
  try {
    view.dispatch({ effects: EditorView.scrollIntoView(pos, { y: 'center' }) });
  } catch { /* view torn down, or the offset no longer exists */ }
}

/**
 * A change this editor did NOT originate — text handed to it by the panel
 * because a snapshot arrived from the cloud. Walter 2026-09-19 (#239), his
 * own design call: *"why don't we add a flag to each receive and save which
 * indicates if this window was the source of any changes. If not it has no
 * say."*
 *
 * The listener below fires onChange for every document change, including the
 * value-sync one. The panels' onChange saves — so a window nobody touched
 * re-saved what it had just been given, roughly 4s later (the debounce). His
 * log is full of the pair:
 *
 *   21:02:14  saved w=iqubs/pwa chars=33895
 *   21:02:35  saved w=md4rq/tab chars=33895
 *
 * Two writers for one edit, each able to race the other — and the same
 * echo-save that #238 catches moving the base backwards. This is also a
 * REGRESSION: the snapshot path's auto-save was deliberately removed on
 * 2026-05-18 (see ListPanel's note) and came back in through the CM6
 * editor's onChange.
 *
 * Annotated, so the panel can tell "the cloud told us" from "we changed it".
 * Everything this window originates — typing, the format menu, the bell-line
 * tidy-up, organize — carries NO annotation and still saves exactly as before.
 */
export const remoteSync = Annotation.define();

export function setFindMatches(view, ranges, current) {
  if (!view) return;
  try {
    view.dispatch({ effects: setFindMatchesEffect.of({ ranges, current }) });
  } catch { /* view torn down */ }
}

// Apply an externally-computed document rewrite to the view as ONE minimal
// transaction (common prefix/suffix diff) with the caret mapped — the Format
// menu's commit path in live-preview mode (Walter 2026-07-16). Dispatching to
// the view (instead of replacing `localContent`) is the #132/#136 lesson: the
// view owns the doc while focused; onChange then syncs the mirror + save.
export function dispatchDocReplace(view, nextValue, selStart, selEnd) {
  const cur = view.state.doc.toString();
  const len = nextValue.length;
  const anchor = Math.max(0, Math.min(selStart, len));
  const head = Math.max(0, Math.min(selEnd == null ? selStart : selEnd, len));
  if (cur === nextValue) {
    view.dispatch({ selection: { anchor, head } });
    return;
  }
  let a = 0;
  const maxA = Math.min(cur.length, len);
  while (a < maxA && cur.charCodeAt(a) === nextValue.charCodeAt(a)) a++;
  let curEnd = cur.length, nextEnd = len;
  while (curEnd > a && nextEnd > a && cur.charCodeAt(curEnd - 1) === nextValue.charCodeAt(nextEnd - 1)) { curEnd--; nextEnd--; }
  view.dispatch({
    changes: { from: a, to: curEnd, insert: nextValue.slice(a, nextEnd) },
    selection: { anchor, head },
    userEvent: 'input',
  });
}

const theme = EditorView.theme({
  '&': { color: 'var(--text)', backgroundColor: 'transparent', height: '100%' },
  '&.cm-focused': { outline: 'none' },
  // min-height makes the editable area fill its container so a tap/click
  // anywhere in the tile lands in the editor and opens the keyboard —
  // without it, only the ~8 lines of text are focusable and the rest of
  // the box is dead space (the "can't edit" report on mobile).
  // Padding lives on the CONTENT, not the host wrapper — the scroller must
  // span the full tile so its scrollbar hugs the tile edge like the
  // textarea's did (Walter 2026-07-19: "scroll bars all in the wrong
  // place"). Panels set --md-editor-pad on the wrapper.
  '.cm-content': { fontFamily: 'var(--mono)', padding: 'var(--md-editor-pad, 0px)', minHeight: '240px', caretColor: 'var(--accent)' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--accent)', borderLeftWidth: '2px' },
  '.cm-scroller': { minHeight: '100%' },
  '.cm-line': { padding: '0 2px' },
});

const MarkdownEditor = forwardRef(function MarkdownEditor(
  { value = '', onChange, onBlur, onFocus, onKeyboardHide, captureLine, readOnly = false, autoFocus = false, listId = null }, ref,
) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const cbRef = useRef({ onChange, onBlur, onFocus, onKeyboardHide, captureLine });
  // Walter 2026-09-14 (reminder proposal step 3, his "i"): the start of the
  // line HE is editing, in the latest document. Leaving a line may apply a
  // bell-line receipt only when this matches, so clicking through old lines
  // never rewrites them. Cleared whenever the caret leaves a line.
  const editedLineFromRef = useRef(null);
  cbRef.current = { onChange, onBlur, onFocus, onKeyboardHide, captureLine };
  // Which list this editor is showing, for "open the agenda at the reminder
  // my cursor is on" (Walter 2026-10-09). A ref because the update listener
  // below is created once at mount. Null in the sandbox / anywhere the
  // caller doesn't know, and then nothing is remembered.
  const listIdRef = useRef(listId);
  listIdRef.current = listId;
  // Identifies THIS editor in the caret registry, so unmounting clears only
  // its own entry - board tiles stay mounted behind an open list.
  const editorIdRef = useRef(`md-${Math.random().toString(36).slice(2)}`);
  // readOnly must be RECONFIGURABLE after mount (Walter 2026-07-19: the edit
  // lock's "other device is typing" flip did nothing in CM6 — the editable
  // extension was baked in by the mount-once effect, so a lock that arrived
  // later was detected but never enforced; the textarea path enforced it via
  // its React readOnly attr). A Compartment lets the readOnly effect below
  // swap the extension in place.
  const editableCompartment = useRef(new Compartment()).current;
  // The last doc string the editor itself produced. We compare incoming
  // `value` against this so we NEVER replace the whole doc in response to
  // our own onChange echo — a full-doc replace mid-keystroke aborts IME
  // composition on mobile and makes the field feel uneditable.
  const lastValueRef = useRef(value);

  useImperativeHandle(ref, () => ({
    focus() { viewRef.current && viewRef.current.focus(); },
    getValue() { return viewRef.current ? viewRef.current.state.doc.toString() : ''; },
    getSelection() {
      const v = viewRef.current; if (!v) return null;
      const s = v.state.selection.main; return { from: s.from, to: s.to };
    },
    setSelection(from, to) {
      const v = viewRef.current; if (!v) return;
      v.dispatch({ selection: { anchor: from, head: to == null ? from : to } });
    },
    insertText(text) {
      const v = viewRef.current; if (!v) return;
      const s = v.state.selection.main;
      v.dispatch({ changes: { from: s.from, to: s.to, insert: text }, selection: { anchor: s.from + text.length } });
    },
    coordsAtPos(pos) { return viewRef.current ? viewRef.current.coordsAtPos(pos) : null; },
    // Format actions — for a toolbar / mobile buttons / the app's Format menu.
    bold() { if (viewRef.current) wrapSelection(viewRef.current, '**'); },
    italic() { if (viewRef.current) wrapSelection(viewRef.current, '*'); },
    strike() { if (viewRef.current) wrapSelection(viewRef.current, '~~'); },
    link() { if (viewRef.current) insertLink(viewRef.current); },
    get view() { return viewRef.current; },
  }), []);

  useEffect(() => {
    const updateListener = EditorView.updateListener.of((u) => {
      // Typing / caret moves while the keyboard is up must keep the caret
      // visible too — CM6's own scroll-into-view can't see the keyboard
      // (Walter 2026-07-17 s1 round 2). ensureCaretVisible no-ops on desktop.
      if ((u.docChanged || u.selectionSet) && typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(ensureCaretVisible);
      }
      // Walter 2026-10-09: remember the reminder line the caret is on, so the
      // agenda can open AT it - "I do this quite a lot and then have to look
      // for that item". Read here rather than when the agenda opens, because
      // clicking the agenda button has already blurred this editor by then.
      if ((u.docChanged || u.selectionSet) && listIdRef.current) {
        try {
          const head = u.state.selection.main.head;
          const lineText = u.state.doc.lineAt(head).text;
          // Walter 2026-10-09 (second pass): the mark FOLLOWS the caret - set
          // while it is on a reminder line, cleared the moment it leaves one.
          // "only if I'm on that line right now".
          setCaretLine(isBellLine(lineText)
            ? { listId: listIdRef.current, lineText, owner: editorIdRef.current }
            : null);
        } catch { /* never let this break the editor */ }
      }
      // Leaving a line = that line is done → capture it as a reminder, same
      // rules as the Enter path. Walter 2026-07-18: tapping ANOTHER line
      // fires neither blur nor keyboard-fold, so a finished reminder line sat
      // raw until some other capture point. Reminder rewrites only
      // (res.commit present) — log @date stamping stays Enter/blur-scoped so
      // merely clicking through old lines can't stamp them. Deferred via
      // setTimeout: no dispatch inside an update listener.
      if (u.selectionSet && !u.docChanged) {
        const cap = cbRef.current.captureLine;
        const oldSel = u.startState.selection.main;
        const newHead = u.state.selection.main.head;
        if (typeof cap === 'function' && oldSel.empty) {
          const oldLine = u.state.doc.lineAt(Math.min(oldSel.head, u.state.doc.length));
          if (u.state.doc.lineAt(newHead).number !== oldLine.number) {
            let res = null;
            // Pass the document ABOVE the line, same as the Enter path — a
            // line whose paragraph starts with a 🔔 is a note on that
            // reminder, not a reminder of its own. Without it this trigger
            // ignored the paragraph rule entirely, so a note turned into a
            // reminder the moment the caret left it (Walter 2026-08-25:
            // "when i click away to almost anywhere else… the note turns
            // into a reminder").
            // Walter 2026-09-14: tell capture whether HE edited this line during
            // the visit - only then may a bell-line receipt apply on leaving it.
            // Per visit: the mark is cleared as the caret leaves.
            const wasEdited = editedLineFromRef.current === oldLine.from;
            editedLineFromRef.current = null;
            try { res = cap(oldLine.text, u.state.doc.sliceString(0, oldLine.from), { wasEdited }); } catch { res = null; }
            if (res && typeof res.newLine === 'string' && typeof res.commit === 'function'
                && res.newLine !== oldLine.text) {
              setTimeout(() => {
                const v = viewRef.current;
                if (!v) return;
                // Re-locate the departed line; bail if the doc moved on.
                if (oldLine.from > v.state.doc.length) return;
                const ln = v.state.doc.lineAt(oldLine.from);
                if (ln.text !== oldLine.text) return;
                v.dispatch({ changes: { from: ln.from, to: ln.to, insert: res.newLine }, userEvent: 'input' });
                // That rewrite is an 'input' event with the caret already on the
                // NEW line, so the listener just marked that line as edited.
                // He hasn't touched it - clear the mark.
                editedLineFromRef.current = null;
                try { res.commit(); } catch { /* create is best-effort */ }
              }, 0);
            }
          }
        }
      }
      // Walter 2026-09-14: remember the line HE is editing. Only user input
      // counts - a remote adopt or value sync dispatches without a user event
      // and must never mark a line. A non-user change instead carries the
      // remembered position through the edit, so text arriving above can't
      // slide a different line into the remembered spot.
      if (u.docChanged) {
        const userEdit = u.transactions.some(tr => tr.isUserEvent('input') || tr.isUserEvent('delete'));
        if (userEdit) {
          editedLineFromRef.current = u.state.doc.lineAt(u.state.selection.main.head).from;
        } else if (editedLineFromRef.current != null) {
          const mapped = u.changes.mapPos(editedLineFromRef.current, -1);
          editedLineFromRef.current = u.state.doc.lineAt(Math.min(mapped, u.state.doc.length)).from;
        }
      }
      if (!u.docChanged) return;
      const doc = u.state.doc.toString();
      lastValueRef.current = doc; // remember our own output so the value-sync effect ignores it
      // #239: tell the panel whether this window originated the change. A
      // value-sync from a snapshot carries the remoteSync annotation; a
      // keystroke, format command, bell-line tidy-up or organize does not.
      const fromRemote = u.transactions.some(tr => tr.annotation(remoteSync) === true);
      if (cbRef.current.onChange) cbRef.current.onChange(doc, { remote: fromRemote });
    });
    // Keep the caret above the on-screen keyboard (Walter 2026-07-17 s1 —
    // landscape phone: the keyboard covered the caret until you scrolled by
    // hand). The keyboard doesn't shrink the layout viewport on iOS (and only
    // sometimes on Android), so CM6's own scroll-into-view can't know about
    // it — the VisualViewport is the real "space above the keyboard". On
    // focus and on every VisualViewport resize (keyboard open / rotate),
    // nudge the scroller so the caret sits above the keyboard's top edge.
    const ensureCaretVisible = () => {
      const v = viewRef.current;
      if (!v || !v.hasFocus) return;
      const vp = typeof window !== 'undefined' ? window.visualViewport : null;
      if (!vp) return;
      // Only act while the keyboard is actually up (same >40px signal as
      // MobileFormatBar's useKeyboardOffset) — desktop stays untouched.
      const kb = window.innerHeight - vp.height - vp.offsetTop;
      if (kb <= 40) {
        if (v.scrollDOM.style.paddingBottom) v.scrollDOM.style.paddingBottom = '';
        return;
      }
      // The mobile format bar rides ABOVE the keyboard — the caret must clear
      // the bar's top edge, not just the keyboard's (Walter 2026-07-19: in
      // portrait the caret sat behind the bar).
      const bar = document.querySelector('[data-mobile-format-bar]');
      const barRect = bar ? bar.getBoundingClientRect() : null;
      // Let the editor scroll its last lines above the keyboard + bar even
      // when the doc is short — the padding is transparent scroll room,
      // cleared by the resize handler when the keyboard goes away.
      const pad = kb + (barRect ? Math.ceil(barRect.height) : 0);
      if (v.scrollDOM.style.paddingBottom !== `${pad}px`) v.scrollDOM.style.paddingBottom = `${pad}px`;
      let c = null;
      try { c = v.coordsAtPos(v.state.selection.main.head); } catch { c = null; }
      if (!c) return;
      const limit = Math.min(vp.offsetTop + vp.height - 28, barRect ? barRect.top - 8 : Infinity);
      let need = c.bottom - limit;
      if (need <= 0) return;
      // Consume the delta in the editor's own scroller first, then in ANY
      // scrollable ancestor (e.g. the board canvas — in landscape the whole
      // tile body can sit under the keyboard, and only an ancestor scroll can
      // move it up). Walter 2026-07-17 s1 round 2: "still doesn't scroll when
      // typing".
      let el = v.scrollDOM;
      while (el && need > 1) {
        const canScroll = el.scrollHeight - el.clientHeight - el.scrollTop;
        if (canScroll > 0) {
          const take = Math.min(need, canScroll);
          el.scrollTop += take;
          need -= take;
        }
        el = el.parentElement;
      }
      // Landscape reality check (Walter 2026-07-18): the app is a fixed
      // 100dvh layout — often NOTHING above can scroll, and the thing the
      // user moves "by hand" is the VISUAL VIEWPORT PAN, which script can't
      // set directly. Chromium's native scrollIntoView is the one lever that
      // pans it, so when manual scrolling couldn't lift the caret, hand the
      // caret's line DOM to the browser.
      if (need > 1) {
        try {
          const at = v.domAtPos(v.state.selection.main.head);
          const base = at.node.nodeType === 1 ? at.node : at.node.parentElement;
          const lineEl = base && base.closest ? base.closest('.cm-line') : null;
          if (lineEl) lineEl.scrollIntoView({ block: 'center', inline: 'nearest' });
        } catch { /* best-effort */ }
      }
    };
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    // On phones, folding the keyboard (its own chevron) or tapping dead space
    // does NOT blur the editor, so blur-time work (reminder capture) never ran
    // — Walter 2026-07-17: "a line should register when I click away". Treat
    // the keyboard CLOSING as that signal: fire onKeyboardHide on the
    // open→closed transition while the editor holds focus.
    let lastKb = 0;
    const onVVResize = () => {
      if (vv) {
        const kb = window.innerHeight - vv.height - vv.offsetTop;
        if (lastKb > 40 && kb <= 40 && viewRef.current
            && (viewRef.current.hasFocus || document.activeElement === viewRef.current.contentDOM)) {
          if (cbRef.current.onKeyboardHide) cbRef.current.onKeyboardHide();
        }
        lastKb = kb;
      }
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(ensureCaretVisible);
      else ensureCaretVisible();
    };
    if (vv) vv.addEventListener('resize', onVVResize);
    const view = new EditorView({
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          findMatchField,
          // Enter capture — when the host's captureLine() decides the current
          // line is a reminder, rewrite it to the canonical 🔔 line AND insert
          // the newline in ONE atomic transaction (race-free — no snapshot, no
          // stale positions), then fire the doc-create side effect. Otherwise
          // return false so Enter behaves normally. (Walter 2026-07-15.)
          keymap.of([{ key: 'Enter', run: (view) => {
            const cap = cbRef.current.captureLine;
            if (typeof cap !== 'function') return false;
            const sel = view.state.selection.main;
            if (!sel.empty) return false;                // only on a collapsed caret
            const line = view.state.doc.lineAt(sel.head);
            if (sel.head !== line.to) return false;      // only at END of line (don't hijack a mid-line split)
            let res;
            // Everything above this line, so capture can tell a note from a
            // reminder — a line whose paragraph starts with a 🔔 belongs to
            // that reminder (Walter 2026-08-25).
            try { res = cap(line.text, view.state.doc.sliceString(0, line.from)); } catch { res = null; }
            if (!res || typeof res.newLine !== 'string') return false;
            view.dispatch({
              changes: { from: line.from, to: line.to, insert: res.newLine + '\n' },
              selection: { anchor: line.from + res.newLine.length + 1 },
              userEvent: 'input',
            });
            if (typeof res.commit === 'function') { try { res.commit(); } catch { /* create is best-effort */ } }
            return true;
          } }]),
          // Walter 2026-08-21: give the phone its keyboard niceties back.
          // CodeMirror turns ALL of these off by default (see its
          // updateAttrs: spellcheck false, autocorrect off, autocapitalize
          // off, writingsuggestions false) because an OS rewriting text
          // behind an editor's back can desync its document model. Nobody
          // chose that for listicity — Walter simply lost double-space-period,
          // automatic "I" and red squiggles the moment he enabled the beta
          // editor, since the plain textarea it replaced had spellCheck on.
          // The facet is applied AFTER the defaults, so these win.
          //
          // The known risk is iOS smart punctuation rewriting straight quotes
          // and dashes; his syntax uses ^ # and [x], which it should leave
          // alone. His call: turn all four on and live with it for a bit.
          // Reverting is one line per attribute.
          //
          // Walter 2026-09-12: writingsuggestions was switched OFF on dev as
          // an experiment for the vanishing `+++++` (#229) and did NOT fix
          // it — restored. His follow-up screenshot showed the pluses drawn
          // in DIFFERENT COLOURS per character, which no text-assistance
          // setting does; see the #229/#230 ledger entries for where that led.
          EditorView.contentAttributes.of({
            spellcheck: 'true',
            autocorrect: 'on',
            autocapitalize: 'sentences',
            writingsuggestions: 'true',
          }),
          keymap.of(formatKeymap),                       // Ctrl/Cmd-B/I/K — before defaults so they win
          keymap.of([...defaultKeymap, ...historyKeymap]),
          EditorView.lineWrapping,
          livePreview,
          theme,
          updateListener,
          editableCompartment.of(EditorView.editable.of(!readOnly)),
          EditorView.domEventHandlers({
            blur: () => { if (cbRef.current.onBlur) cbRef.current.onBlur(); return false; },
            focus: () => {
              if (cbRef.current.onFocus) cbRef.current.onFocus();
              // After the keyboard's open animation settles (~300ms on both
              // platforms), make sure the caret isn't under it.
              setTimeout(ensureCaretVisible, 350);
              return false;
            },
            // Click a rendered link → open it. Everywhere else falls through to
            // normal caret placement, so the rest of the line stays editable.
            mousedown: (e) => {
              const a = e.target && e.target.closest && e.target.closest('.md-link[data-url]');
              if (a) {
                let url = a.getAttribute('data-url');
                if (!/^[a-z]+:\/\//i.test(url)) url = 'https://' + url;
                window.open(url, '_blank', 'noopener,noreferrer');
                e.preventDefault();
                return true;
              }
              return false;
            },
          }),
        ],
      }),
      parent: hostRef.current,
    });
    viewRef.current = view;
    if (autoFocus) view.focus();
    // Make this editor findable. Ctrl+F searches textareas, and CM6 isn't one
    // — so without this the find bar reports 0/0 in every list (Walter
    // 2026-08-23). See lib/cmRegistry.
    const unregister = registerEditorView(view);
    const editorId = editorIdRef.current;
    return () => {
      if (vv) vv.removeEventListener('resize', onVVResize);
      unregister();
      // This editor is going away, so its caret is too (#248). Only OUR entry:
      // another list's editor may legitimately hold the caret right now.
      clearCaretLineFor(editorId);
      view.destroy();
      viewRef.current = null;
    };
  }, []); // eslint-disable-line

  // Sync readOnly → editor. The edit lock flips the panels' effectiveReadOnly
  // AFTER mount when another device starts typing — the editor must actually
  // stop accepting input then, matching the textarea's readOnly attr
  // (Walter 2026-07-19: "I can edit on both at the same time").
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: editableCompartment.reconfigure(EditorView.editable.of(!readOnly)) });
  }, [readOnly, editableCompartment]);

  // Sync EXTERNAL value → editor (e.g. a Firestore snapshot arriving).
  // Skip when `value` is just the echo of what we last emitted — replacing
  // the doc on our own keystroke would fight the caret and kill IME.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === lastValueRef.current) return;
    const cur = view.state.doc.toString();
    lastValueRef.current = value;
    // Annotated as NOT ours (#239) — the panel applies it to the screen and
    // must not save it back. See the remoteSync note above.
    if (value !== cur) {
      view.dispatch({
        changes: { from: 0, to: cur.length, insert: value },
        annotations: remoteSync.of(true),
      });
    }
  }, [value]);

  return <div ref={hostRef} className="md-editor" />;
});

export default MarkdownEditor;
